import { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { LazyLoadedImage } from '../../Components/LazyLoadedImage';
import { getImagesFromFolder } from '../../Firebase/firebase';

const NII_PASSKEY = 'F0rY0ur3y3s!';
const NII_CATEGORIES_ALL = [
  // 'AdinkraWorld', // disabled for now
  'Afronaut Travelers',
  'Akashic Memories',
  'Noir',
  'Black Gold',
  'Collage',
  'Cosmic Geometry',
  'Golden Guards',
  'Kente Eagle',
  'Kente Pride',
  'Mystique',
  'Beyond Reality',
  'Azul Royale',
  'Afro Samurai',
  'Wide Trip',
];
const NII_CATEGORIES = NII_CATEGORIES_ALL;

const NiiPage = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [passkeyInput, setPasskeyInput] = useState('');
  const [passkeyError, setPasskeyError] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(NII_CATEGORIES[0]);
  const [imageUrls, setImageUrls] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [passkeyModalOpen, setPasskeyModalOpen] = useState(true);

  // Check sessionStorage for previous authentication
  useEffect(() => {
    const stored = sessionStorage.getItem('nii_authenticated');
    if (stored === 'true') {
      setIsAuthenticated(true);
      setPasskeyModalOpen(false);
    }
  }, []);

  const handlePasskeySubmit = (e) => {
    e.preventDefault();
    if (passkeyInput === NII_PASSKEY) {
      setIsAuthenticated(true);
      setPasskeyModalOpen(false);
      setPasskeyError(false);
      sessionStorage.setItem('nii_authenticated', 'true');
    } else {
      setPasskeyError(true);
    }
  };

  const handlePasskeyClose = () => {
    setPasskeyModalOpen(true);
  };

  // Fetch images when category changes
  useEffect(() => {
    if (!isAuthenticated) return;

    const fetchImages = async () => {
      setIsLoading(true);
      try {
        const folderPath = `images/${selectedCategory}`;
        const urls = await getImagesFromFolder(folderPath);
        setImageUrls(urls);
      } catch (error) {
        console.error('Error fetching images:', error);
        setImageUrls([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchImages();
  }, [isAuthenticated, selectedCategory]);

  // Show passkey modal until authenticated
  if (!isAuthenticated) {
    return (
      <Dialog
        open={passkeyModalOpen}
        onClose={handlePasskeyClose}
        PaperProps={{
          sx: {
            backgroundColor: '#1a1a1a',
            borderRadius: '16px',
            p: 2,
            minWidth: '320px',
          },
        }}
      >
        <DialogTitle
          sx={{
            color: 'white',
            fontFamily: 'Montserrat',
            textAlign: 'center',
          }}
        >
          Enter Passkey
        </DialogTitle>
        <DialogContent>
          <Box
            component="form"
            onSubmit={handlePasskeySubmit}
            sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}
          >
            <TextField
              type="password"
              label="Passkey"
              value={passkeyInput}
              onChange={(e) => {
                setPasskeyInput(e.target.value);
                setPasskeyError(false);
              }}
              error={passkeyError}
              helperText={passkeyError ? 'Incorrect passkey' : ''}
              fullWidth
              autoFocus
              sx={{
                '& .MuiOutlinedInput-root': {
                  color: 'white',
                  '& fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
                },
                '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' },
              }}
            />
            <Button
              type="submit"
              variant="contained"
              sx={{
                fontFamily: 'Montserrat',
                backgroundColor: '#333',
                '&:hover': { backgroundColor: '#555' },
              }}
            >
              Enter
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Box
      sx={{
        height: '100vh',
        overflowY: 'auto',
        mb: 0,
        backgroundColor: 'black',
      }}
    >
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 1000,
          backgroundColor: 'black',
          p: 2,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
          }}
        >
          <FormControl
            sx={{
              minWidth: 220,
              '& .MuiOutlinedInput-root': {
                color: 'white',
                '& fieldset': { borderColor: 'rgba(255,255,255,0.5)' },
              },
              '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.8)' },
              '& .MuiSvgIcon-root': { color: 'rgba(255,255,255,0.8)' },
            }}
          >
            <InputLabel id="nii-category-select-label">Category</InputLabel>
            <Select
              labelId="nii-category-select-label"
              value={selectedCategory}
              label="Category"
              onChange={(e) => setSelectedCategory(e.target.value)}
            >
              {NII_CATEGORIES.map((cat) => (
                <MenuItem key={cat} value={cat}>
                  {cat}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'center',
          alignItems: 'center',
          padding: 2,
          gap: 2,
          width: '100%',
          boxSizing: 'border-box',
        }}
      >
        {isLoading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: '100%',
              minHeight: '300px',
            }}
          >
            <CircularProgress sx={{ color: 'white' }} />
          </Box>
        ) : imageUrls.length === 0 ? (
          <Typography
            sx={{
              color: 'rgba(255,255,255,0.7)',
              fontFamily: 'Montserrat',
              textAlign: 'center',
              mt: 4,
            }}
          >
            No images in this category yet. Upload images to Firebase Storage at{' '}
            <Box component="span" sx={{ fontFamily: 'monospace' }}>
              images/{selectedCategory}/
            </Box>
          </Typography>
        ) : (
          imageUrls.map((src, index) => (
            <Box
              key={`${selectedCategory}-${index}`}
              sx={{
                position: 'relative',
                display: 'flex',
                justifyContent: 'center',
                width: { xs: '100%', md: 'auto' },
                boxSizing: 'border-box',
              }}
            >
              <LazyLoadedImage
                src={src}
                alt={`${selectedCategory} artwork ${index + 1}`}
                collection={imageUrls}
                showTitle={false}
                fullWidthMobile
              />
            </Box>
          ))
        )}
      </Box>
    </Box>
  );
};

export default NiiPage;
